import * as Sentry from "@sentry/react";
import type { KeycloakProfile } from "keycloak-js";
import type { User } from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";
import { isLocalhost } from "@/utils";

export function initSentry(router: AnyRouter) {
	// Don't initialize Sentry on localhost or if no DSN is provided
	if (isLocalhost() || !import.meta.env.VITE_SENTRY_DSN) {
		console.log("[Sentry] Skipped initialization on localhost");
		return;
	}

	Sentry.init({
		dsn: import.meta.env.VITE_SENTRY_DSN,
		environment: import.meta.env.MODE, // "development" | "production"
		release: import.meta.env.VITE_SENTRY_RELEASE as string, // optional: match your CI release
		integrations: [
			// TanStack Router integration for navigation tracking
			Sentry.tanstackRouterBrowserTracingIntegration(router),
			// Session Replay (optional - see section 6)
			// Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
		],
		tracesSampleRate: 0.1, // tune for prod; 0.1 = 10% of transactions
		// replaysSessionSampleRate: 0.1,     // if you enable Replay
		// replaysOnErrorSampleRate: 1.0,
		// Good place to drop noisy or sensitive events:
		beforeSend(event) {
			// Example: drop network noise or redact PII
			// if (event.exception?.values?.[0]?.value?.includes("ResizeObserver")) return null;
			// redact
			if (event.user?.email) event.user.email = "[REDACTED]";
			return event;
		},
	});
}

// Set user context from Keycloak profile
export function captureUserContext(profile: KeycloakProfile | null) {
	if (!profile) {
		Sentry.setUser(null);
		return;
	}

	const sentryUser: User = {
		id: profile.id,
		username: profile.username,
	};

	// Add additional data if available
	if (profile.firstName || profile.lastName) {
		sentryUser.name =
			`${profile.firstName || ""} ${profile.lastName || ""}`.trim();
	}

	Sentry.setUser(sentryUser);
}

// Clear user context on logout
export function clearUserContext() {
	Sentry.setUser(null);
	Sentry.setContext("user_profile", null);
}
